import{getHeroCombatKit,type HeroCombatId,type SignatureAttackKind}from'./heroCombatIdentity'
import{isRigAnimationClipQuarantined}from'./rigAnimationQuarantine'

export type ClipConfidence='exact'|'strong'|'weak'|'fallback'
export type SkillClipBinding={
  attack:SignatureAttackKind
  skillName:string
  clip:string|null
  confidence:ClipConfidence
  score:number
  reasons:readonly string[]
}

const ATTACK_ALIASES:Record<SignatureAttackKind,readonly string[]>={
  light:['light','jab','quick','fast','punch','slash','strike','attack1','atk1','combo1'],
  heavy:['heavy','strong','power','smash','slam','attack2','atk2','combo2'],
  special:['special','skill','super','ultimate','magic','burst','attack3','atk3','combo3'],
  throw:['throw','grab','grapple','toss','clinch','capture']
}

const NEGATIVE:Record<SignatureAttackKind,readonly string[]>={
  light:['idle','walk','run','jump','death','hit','hurt','block','heavy','throw'],
  heavy:['idle','walk','run','jump','death','hit','hurt','block','light','throw'],
  special:['idle','walk','run','jump','death','hit','hurt','block','throw'],
  throw:['idle','walk','run','jump','death','hit','hurt','block','light','heavy']
}

export function normalizeAnimationClipName(name:string){
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g,' ').replace(/\s+/g,' ').trim()
}
export function isQuarantinedHeroClip(heroId:HeroCombatId,name:string){return isRigAnimationClipQuarantined(heroId,name)}

function tokens(name:string){return normalizeAnimationClipName(name).split(' ').filter(Boolean)}

function scoreClip(attack:SignatureAttackKind,clip:string){
  const normalized=normalizeAnimationClipName(clip),parts=tokens(clip),aliases=ATTACK_ALIASES[attack]
  let score=0
  const reasons:string[]=[]
  for(const alias of aliases){
    if(normalized===alias){score+=12;reasons.push(`exact:${alias}`)}
    else if(parts.includes(alias)){score+=7;reasons.push(`token:${alias}`)}
    else if(normalized.includes(alias)){score+=3;reasons.push(`partial:${alias}`)}
  }
  for(const negative of NEGATIVE[attack])if(parts.includes(negative)){score-=8;reasons.push(`reject:${negative}`)}
  if(/(^|\s)(attack|atk)\s*0?1($|\s)/.test(normalized)&&attack==='light'){score+=6;reasons.push('ordinal:1')}
  if(/(^|\s)(attack|atk)\s*0?2($|\s)/.test(normalized)&&attack==='heavy'){score+=6;reasons.push('ordinal:2')}
  if(/(^|\s)(attack|atk)\s*0?3($|\s)/.test(normalized)&&attack==='special'){score+=6;reasons.push('ordinal:3')}
  return{score,reasons}
}

function confidence(score:number):ClipConfidence{
  if(score>=12)return'exact'
  if(score>=7)return'strong'
  if(score>=3)return'weak'
  return'fallback'
}

export function resolveHeroAnimationBindings(heroId:HeroCombatId,clipNames:readonly string[]):SkillClipBinding[]{
  if(clipNames.length>512)throw new Error('Animation clip inventory exceeds resolver limit')
  const clean=[...new Set(clipNames.map(v=>v.trim()).filter(v=>Boolean(v)&&!isQuarantinedHeroClip(heroId,v)))]
  const kit=getHeroCombatKit(heroId)
  const attacks:SignatureAttackKind[]=['light','heavy','special','throw']
  const used=new Set<string>()
  return attacks.map(attack=>{
    let best:string|null=null,bestScore=Number.NEGATIVE_INFINITY,bestReasons:string[]=[]
    for(const clip of clean){
      if(used.has(clip))continue
      const result=scoreClip(attack,clip)
      if(result.score>bestScore||(result.score===bestScore&&best!==null&&clip.localeCompare(best)<0)){
        best=clip;bestScore=result.score;bestReasons=result.reasons
      }
    }
    if(best===null||bestScore<3)return{attack,skillName:kit.skills[attack].name,clip:null,confidence:'fallback',score:Math.max(0,bestScore===Number.NEGATIVE_INFINITY?0:bestScore),reasons:['no-safe-match']}
    used.add(best)
    return{attack,skillName:kit.skills[attack].name,clip:best,confidence:confidence(bestScore),score:bestScore,reasons:bestReasons}
  })
}
